import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EmployeeBusinessCardLinkSchema,
  EmployeeGradeSchema,
  EmployeeSchema,
  type EmployeeDto,
  type EmployeeQualificationDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { getData, patchJson, postJson } from '../../shared/api/client';
import { daysBetween } from '../../shared/documents/dates';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog } from '../../shared/ui/forms';
import { QrCode, qrPng } from '../../shared/ui/QrCode';
import { EmptyState, Section } from '../../shared/ui/page';
import { Facts } from '../finance/detail';
import { businessCardFileName, businessCardUrl } from './business-card';

const EMPLOYEES = '/api/v1/hr/employees';

/**
 * The employee profile details (FRD-HRP-072 to HRP-079, CLR-40): what each field is called, how it
 * is entered and which section it belongs to. Date of birth, the bank details and the insurance
 * identifiers are designated sensitive, so most sessions receive them masked.
 */
export const PROFILE_GROUPS = [
  {
    title: 'Personal',
    label: 'Personal details',
    fields: [
      ['birthDate', 'Date of birth', 'date'],
      ['nationality', 'Nationality', 'text'],
      ['gradeCode', 'Grade', 'grade'],
      // CLR-47: for the workforce charts of the HR, CEO and COO dashboards.
      ['gender', 'Gender', 'gender'],
      ['educationLevelCode', 'Highest education', 'education'],
    ],
  },
  {
    title: 'Home country',
    label: 'Home country details',
    fields: [
      ['homeAddress', 'Home country address', 'multiline'],
      ['homeCountry', 'Home country', 'text'],
      ['homePhone', 'Home country contact number', 'text'],
    ],
  },
  {
    title: 'Bank details',
    label: 'Bank details',
    fields: [
      ['bankName', 'Bank name', 'text'],
      ['bankAccountHolder', 'Account holder', 'text'],
      ['bankIban', 'IBAN', 'text'],
    ],
  },
  {
    title: 'Insurance',
    label: 'Insurance details',
    fields: [
      ['insuranceProvider', 'Insurance provider', 'text'],
      ['insurancePolicyNumber', 'Policy number', 'text'],
      ['insuranceCardNumber', 'Card number', 'text'],
      ['insuranceExpiryDate', 'Insurance expiry date', 'date'],
    ],
  },
] as const;

export type ProfileField = (typeof PROFILE_GROUPS)[number]['fields'][number][0];
export const PROFILE_FIELDS: readonly ProfileField[] = PROFILE_GROUPS.flatMap((group) =>
  group.fields.map(([field]) => field),
);

/** The configured education levels (list `hr.education_level`, CLR-47), inactive ones marked. */
export function useEducationLevels(enabled = true) {
  return useQuery({
    queryKey: ['hr', 'education-levels'],
    enabled,
    queryFn: () => getData('/api/v1/hr/education-levels', z.array(EmployeeGradeSchema)),
  });
}

export const GENDER_LABELS: Readonly<Record<string, string>> = { male: 'Male', female: 'Female' };

/** The configured grades (list `hr.employee_grade`), inactive ones marked. */
export function useEmployeeGrades(enabled = true) {
  return useQuery({
    queryKey: ['hr', 'employee-grades'],
    enabled,
    queryFn: () => getData('/api/v1/hr/employee-grades', z.array(EmployeeGradeSchema)),
  });
}

/**
 * Completed service from the joining date to `until` — today, or the exit date of an employee who
 * has left — in whole years, months and days (FRD-HRP-072). Derived, never stored; a joining date
 * still in the future has no service yet. A month is counted on the same day of the month as the
 * joining date, or on the month's last day when it is shorter (joined 31 January: one month on
 * 28 February).
 */
export function serviceLength(joiningDate: string, until: string): string {
  const [jy = 0, jm = 1, jd = 1] = joiningDate.split('-').map(Number);
  const [ty = 0, tm = 1, td = 1] = until.split('-').map(Number);
  const daysIn = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();
  // The day the joining date "falls" on in a given month.
  const dayIn = (year: number, month: number) => Math.min(jd, daysIn(year, month));
  let months = (ty - jy) * 12 + (tm - jm) - (td < dayIn(ty, tm) ? 1 : 0);
  if (months < 0) return 'Not started';
  const anniversary = new Date(Date.UTC(jy, jm - 1 + months, 1));
  const ay = anniversary.getUTCFullYear();
  const am = anniversary.getUTCMonth() + 1;
  const pad = (n: number) => String(n).padStart(2, '0');
  const days = daysBetween(`${String(ay)}-${pad(am)}-${pad(dayIn(ay, am))}`, until);
  const years = Math.floor(months / 12);
  months -= years * 12;
  const plural = (n: number, unit: string) => `${String(n)} ${unit}${n === 1 ? '' : 's'}`;
  const parts = [
    years > 0 ? plural(years, 'year') : '',
    months > 0 ? plural(months, 'month') : '',
    days > 0 ? plural(days, 'day') : '',
  ].filter((part) => part !== '');
  return parts.length === 0 ? 'Joined today' : parts.join(', ');
}

/**
 * The length of service to show: to today, or to the exit date once the employee has left. A
 * masked exit date would show through the length, so that is masked too (CLR-49).
 */
export function serviceShown(
  employee: Pick<EmployeeDto, 'joiningDate' | 'status' | 'exitDate'>,
  today: string,
): string {
  if (employee.status !== 'inactive' || employee.exitDate === null) {
    return serviceLength(employee.joiningDate, today);
  }
  if (masked(employee.exitDate)) return '•••• (masked)';
  return `${serviceLength(employee.joiningDate, employee.exitDate)} (until the exit date)`;
}

const masked = (value: string | null) => value?.startsWith('••••') ?? false;
const shown = (value: string | null) =>
  value === null ? '—' : masked(value) ? `${value} (masked)` : value;

/** The profile sections of the employee record: read-only facts, education and the card code. */
export function EmployeeProfileSections({
  employee,
  canChange,
}: {
  employee: EmployeeDto;
  canChange: boolean;
}) {
  const grades = useEmployeeGrades(employee.gradeCode !== null);
  const levels = useEducationLevels(employee.educationLevelCode !== null);
  const value = (field: ProfileField): string => {
    const raw = employee[field];
    // A masked value is never looked up as a code (CLR-49: gender and education can be masked).
    if (masked(raw)) return shown(raw);
    if (field === 'gradeCode' && raw !== null) {
      return grades.data?.find((grade) => grade.valueCode === raw)?.label ?? raw;
    }
    if (field === 'educationLevelCode' && raw !== null) {
      return levels.data?.find((level) => level.valueCode === raw)?.label ?? raw;
    }
    if (field === 'gender' && raw !== null) return GENDER_LABELS[raw] ?? raw;
    return shown(raw);
  };
  return (
    <>
      {PROFILE_GROUPS.map((group) => (
        <Section key={group.title} title={group.title}>
          <Facts
            label={group.label}
            facts={[
              ...group.fields.map(([field, label]): [string, string] => [label, value(field)]),
            ]}
          />
        </Section>
      ))}
      <Qualifications employee={employee} canChange={canChange} />
      <BusinessCard employee={employee} />
    </>
  );
}

const KIND_LABELS = { education: 'Education', certificate: 'Certificate' } as const;

/** Education and certificates (FRD-HRP-078): a descriptive list kept on the employee record. */
function Qualifications({ employee, canChange }: { employee: EmployeeDto; canChange: boolean }) {
  const [editing, setEditing] = useState(false);
  const entries = employee.qualifications;
  return (
    <Section
      title="Education & certificates"
      actions={
        canChange ? (
          <Button
            size="small"
            variant="outlined"
            onClick={(event) => {
              event.currentTarget.focus();
              setEditing(true);
            }}
          >
            Edit education &amp; certificates
          </Button>
        ) : undefined
      }
    >
      {entries.length === 0 ? (
        <EmptyState
          title="No education or certificates recorded"
          detail="Scanned certificates are attached under Documents."
        />
      ) : (
        <Table size="small" aria-label="Education and certificates">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Qualification or certificate</TableCell>
              <TableCell>Institution or issuer</TableCell>
              <TableCell>Year</TableCell>
              <TableCell>Expiry</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry, index) => (
              // The entries have no identity of their own; their order is their key.
              <TableRow key={index}>
                <TableCell>{KIND_LABELS[entry.kind]}</TableCell>
                <TableCell>{entry.title}</TableCell>
                <TableCell>{entry.institution ?? '—'}</TableCell>
                <TableCell>{entry.year ?? '—'}</TableCell>
                <TableCell>{entry.expiryDate ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {editing && (
        <QualificationsDialog
          key={String(employee.version)}
          employee={employee}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
    </Section>
  );
}

interface Draft {
  kind: 'education' | 'certificate';
  title: string;
  institution: string;
  year: string;
  expiryDate: string;
}
const draftOf = (entry: EmployeeQualificationDto): Draft => ({
  kind: entry.kind,
  title: entry.title,
  institution: entry.institution ?? '',
  year: entry.year === null ? '' : String(entry.year),
  expiryDate: entry.expiryDate ?? '',
});
const validYear = (year: string) =>
  year === '' || (/^\d{4}$/.test(year) && +year >= 1900 && +year <= 2100);

/** Replaces the whole list in one change, with If-Match on the employee's version. */
function QualificationsDialog({
  employee,
  onClose,
}: {
  employee: EmployeeDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Draft[]>(() => employee.qualifications.map(draftOf));
  const update = (index: number, change: Partial<Draft>) => {
    setDrafts((current) => current.map((d, i) => (i === index ? { ...d, ...change } : d)));
  };
  const entries: EmployeeQualificationDto[] = drafts.map((d) => ({
    kind: d.kind,
    title: d.title.trim(),
    institution: d.institution.trim() === '' ? null : d.institution.trim(),
    year: d.year === '' ? null : Number(d.year),
    // Only a certificate expires.
    expiryDate: d.kind === 'certificate' && d.expiryDate !== '' ? d.expiryDate : null,
  }));
  const complete = drafts.every((d) => d.title.trim() !== '' && validYear(d.year));
  const changed = JSON.stringify(entries) !== JSON.stringify(employee.qualifications);

  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${EMPLOYEES}/${employee.id}`,
        { qualifications: entries },
        EmployeeSchema,
        session.data?.csrfToken ?? '',
        employee.version,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'employee'] });
      onClose();
    },
  });

  return (
    <FormDialog
      open
      title="Education & certificates"
      submitLabel="Save"
      canSubmit={complete && changed}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <Alert severity="info" variant="outlined">
        Attach the scanned certificate under Documents; an expiring document raises its own alert.
      </Alert>
      {drafts.map((draft, index) => (
        <Stack
          key={index}
          spacing={1.5}
          role="group"
          aria-label={`Entry ${String(index + 1)}`}
          sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2 }}
        >
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
            <TextField
              select
              label="Type"
              size="small"
              value={draft.kind}
              sx={{ minWidth: 150 }}
              onChange={(e) => {
                update(index, { kind: e.target.value as Draft['kind'] });
              }}
            >
              <MenuItem value="education">Education</MenuItem>
              <MenuItem value="certificate">Certificate</MenuItem>
            </TextField>
            <TextField
              label="Qualification or certificate"
              size="small"
              required
              fullWidth
              value={draft.title}
              slotProps={{ htmlInput: { maxLength: 200 } }}
              onChange={(e) => {
                update(index, { title: e.target.value });
              }}
            />
            <IconButton
              aria-label={`Remove entry ${String(index + 1)}`}
              onClick={() => {
                setDrafts((current) => current.filter((_, i) => i !== index));
              }}
            >
              <DeleteOutlinedIcon />
            </IconButton>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="Institution or issuer"
              size="small"
              fullWidth
              value={draft.institution}
              slotProps={{ htmlInput: { maxLength: 200 } }}
              onChange={(e) => {
                update(index, { institution: e.target.value });
              }}
            />
            <TextField
              label="Year"
              size="small"
              value={draft.year}
              error={!validYear(draft.year)}
              sx={{ minWidth: 110 }}
              slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 4 } }}
              onChange={(e) => {
                update(index, { year: e.target.value.replace(/\D/g, '') });
              }}
            />
            {draft.kind === 'certificate' && (
              <TextField
                label="Expiry date"
                type="date"
                size="small"
                value={draft.expiryDate}
                sx={{ minWidth: 170 }}
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(e) => {
                  update(index, { expiryDate: e.target.value });
                }}
              />
            )}
          </Stack>
        </Stack>
      ))}
      <Box>
        <Button
          startIcon={<AddIcon />}
          disabled={drafts.length >= 50}
          onClick={() => {
            setDrafts((current) => [
              ...current,
              { kind: 'education', title: '', institution: '', year: '', expiryDate: '' },
            ]);
          }}
        >
          Add entry
        </Button>
      </Box>
    </FormDialog>
  );
}

/**
 * The business-card code (FRD-HRP-079, CLR-65). It opens the employee's public card page: the
 * photo, the work contact and a button that saves the contact with the photo, for anyone who
 * scans it. The code can be downloaded as a PNG; a user who may change the record can issue a new
 * link, which makes the old code and link stop working.
 */
function BusinessCard({ employee }: { employee: EmployeeDto }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const mayRenew = hasCapability(session.data, 'hr.employee:change');
  const [renewing, setRenewing] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const key = ['hr', 'employee', employee.id, 'business-card'];
  const link = useQuery({
    queryKey: key,
    queryFn: () =>
      getData(`${EMPLOYEES}/${employee.id}/business-card`, EmployeeBusinessCardLinkSchema),
  });
  const renew = useMutation({
    mutationFn: () =>
      postJson(
        `${EMPLOYEES}/${employee.id}/business-card/renew`,
        {},
        EmployeeBusinessCardLinkSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: (renewed) => {
      queryClient.setQueryData(key, renewed);
      setRenewing(false);
    },
  });
  if (link.isPending) return null;
  if (link.isError) {
    return (
      <Section title="Business card">
        <Alert severity="error" variant="outlined" sx={{ m: 2 }}>
          The business-card link could not be loaded: {link.error.message}
        </Alert>
      </Section>
    );
  }
  const url = businessCardUrl(link.data.token);
  const download = () => {
    setDownloadError(null);
    try {
      const anchor = document.createElement('a');
      anchor.href = qrPng(url);
      anchor.download = businessCardFileName(employee.name);
      anchor.click();
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : String(error));
    }
  };
  return (
    <Section title="Business card">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={3}
        sx={{ p: 2, alignItems: 'center' }}
      >
        <QrCode value={url} label={`Business card QR code of ${employee.name}`} size={168} />
        <Stack spacing={1.5} sx={{ maxWidth: 460, alignItems: 'flex-start' }}>
          <Typography variant="body2" color="text.secondary">
            Scanning this code opens the business card of {employee.name}: photo, name, position,
            company, work e-mail and work telephone, with a button to save the contact. Anyone
            holding the code or its link can open the card without signing in; it shows nothing else
            of this record.
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={download}>
              Download QR code
            </Button>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              href={url}
              target="_blank"
              rel="noopener"
            >
              Open card
            </Button>
            {mayRenew && (
              <Button
                color="warning"
                startIcon={<AutorenewIcon />}
                onClick={() => {
                  renew.reset();
                  setRenewing(true);
                }}
              >
                Issue new link
              </Button>
            )}
          </Stack>
          {downloadError !== null && (
            <Alert severity="error" variant="outlined">
              The QR code image could not be made: {downloadError}
            </Alert>
          )}
        </Stack>
      </Stack>
      <ConfirmDialog
        open={renewing}
        title="Issue a new business-card link?"
        message={`The current code and link of ${employee.name} stop working at once, including printed and shared copies. Share or download the new code afterwards.`}
        confirmLabel="Issue new link"
        tone="warning"
        busy={renew.isPending}
        error={renew.error?.message ?? null}
        onCancel={() => {
          setRenewing(false);
        }}
        onConfirm={() => {
          renew.mutate();
        }}
      />
    </Section>
  );
}
